import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CardActionArea,
} from '@mui/material';
import {
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  Update as UpdateIcon,
  Assessment as AssessmentIcon,
  DirectionsCar as AutoIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  LocalHospital as HealthIcon,
  Favorite as LifeIcon,
  Cake as CakeIcon,
} from '@mui/icons-material';
import MainLayout from '../layouts/MainLayout';
import { dashboardService, DashboardData } from '../services/dashboardService';
import { useNavigate } from 'react-router-dom';

// Interface para os aniversariantes
interface Aniversariante {
  id: number;
  nome: string;
  dataNascimento: string;
}

const HomePage = () => {
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState<DashboardData | null>(null);
  const [aniversariantes, setAniversariantes] = useState<Aniversariante[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const carregarDados = async () => {
      try {
        const mesAtual = new Date().getMonth() + 1;
        // Usando Promise.all para carregar os dados em paralelo
        const [dadosDashboard, dadosAniversariantes] = await Promise.all([
          dashboardService.getDadosTeste(),
          dashboardService.getAniversariantes(mesAtual)
        ]);

        setDados(dadosDashboard);
        setAniversariantes(dadosAniversariantes);

      } catch (error) {
        console.error('Erro ao carregar dados do dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, []);

  const handleCardClick = (tipo: 'vencidas' | 'a_vencer') => {
    if (tipo === 'vencidas') {
      navigate('/apolices?status=Vencida');
    } else if (tipo === 'a_vencer') {
      navigate('/apolices?status=A_Vencer');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (!dados) {
    return null;
  }

  return (
    <MainLayout>
      <Box sx={{ maxWidth: 1200, margin: '40px auto', px: 2 }}>
        <Typography variant="h4" color="primary" gutterBottom>
          Dashboard
        </Typography>

        {/* Cards Principais */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CardActionArea onClick={() => navigate('/clientes')}>
              <Card sx={{ bgcolor: '#1976d2', color: 'white' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <PeopleIcon sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h6">Total de Clientes</Typography>
                  </Box>
                  <Typography variant="h4">{dados.totalClientes}</Typography>
                </CardContent>
              </Card>
            </CardActionArea>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <CardActionArea onClick={() => navigate('/apolices')}>
              <Card sx={{ bgcolor: '#2e7d32', color: 'white' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <AssignmentIcon sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h6">Total de Apólices</Typography>
                  </Box>
                  <Typography variant="h4">{dados.totalApolices}</Typography>
                </CardContent>
              </Card>
            </CardActionArea>
          </Grid>
        </Grid>

        {/* Cards de Alertas */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CardActionArea onClick={() => handleCardClick('vencidas')}>
              <Card sx={{ bgcolor: '#d32f2f', color: 'white' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <WarningIcon sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h6">Apólices Vencidas</Typography>
                  </Box>
                  <Typography variant="h4">{dados.apolicesVencidas}</Typography>
                </CardContent>
              </Card>
            </CardActionArea>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <CardActionArea onClick={() => handleCardClick('a_vencer')}>
              <Card sx={{ bgcolor: '#f57c00', color: 'white' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <UpdateIcon sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h6">A Vencer (Próx. 30 dias)</Typography>
                  </Box>
                  <Typography variant="h4">{dados.apolicesAVencer}</Typography>
                </CardContent>
              </Card>
            </CardActionArea>
          </Grid>
        </Grid>

        {/* Ramos Mais Vendidos */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Ramos Mais Vendidos
                </Typography>
                <List>
                  {dados.ramosMaisVendidos.map((ramo, index) => (
                    <React.Fragment key={ramo.nome}>
                      <ListItem>
                        <ListItemIcon>
                          {ramo.nome === 'Auto' && <AutoIcon color="primary" />}
                          {ramo.nome === 'Residencial' && <HomeIcon color="primary" />}
                          {ramo.nome === 'Vida' && <LifeIcon color="primary" />}
                          {ramo.nome === 'Empresarial' && <BusinessIcon color="primary" />}
                          {ramo.nome === 'Saúde' && <HealthIcon color="primary" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={ramo.nome}
                          secondary={`${ramo.quantidade} apólices`}
                        />
                      </ListItem>
                      {index < dados.ramosMaisVendidos.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Renovações Pendentes
                </Typography>
                <Typography variant="h4" align="center" sx={{ mt: 4, mb: 4 }}>
                  {dados.renovacoesPendentes}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Aniversariantes do Mês */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <CakeIcon color="secondary" sx={{ mr: 1.5 }} />
                  <Typography variant="h6" gutterBottom component="div" sx={{ mb: 0 }}>
                    Aniversariantes do Mês
                  </Typography>
                </Box>
                <List sx={{ maxHeight: 250, overflow: 'auto' }}>
                  {aniversariantes.length > 0 ? (
                    aniversariantes.map((aniversariante, index) => (
                      <React.Fragment key={aniversariante.id}>
                        <ListItem secondaryAction={
                          <Typography variant="body2" color="text.secondary">
                            {new Date(aniversariante.dataNascimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                          </Typography>
                        }>
                          <ListItemText primary={aniversariante.nome} />
                        </ListItem>
                        {index < aniversariantes.length - 1 && <Divider variant="inset" component="li" />}
                      </React.Fragment>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText primary="Nenhum aniversariante este mês." />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default HomePage; 